import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Typography,
  Divider,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  AccountCircle,
  Login,
  PersonAdd,
  Logout,
  Settings,
  Route
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import AuthModal from '../modals/AuthModal';

const UserMenu: React.FC = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signin');
  
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSignIn = () => {
    setAuthMode('signin');
    setAuthModalOpen(true);
    handleMenuClose();
  };

  const handleSignUp = () => {
    setAuthMode('signup');
    setAuthModalOpen(true);
    handleMenuClose();
  };

  const handleSignOut = async () => {
    await signOut();
    handleMenuClose();
  };

  const handleMyRoutes = () => {
    navigate('/my-routes');
    handleMenuClose();
  };

  const handleSettings = () => {
    navigate('/settings');
    handleMenuClose();
  };

  const getUserInitials = (email: string): string => {
    return email.charAt(0).toUpperCase();
  };

  return (
    <>
      <IconButton
        size="large"
        aria-label="user account"
        aria-controls="user-menu"
        aria-haspopup="true"
        onClick={handleMenuOpen}
        color="inherit"
        sx={{ ml: 2 }}
      >
        {user ? (
          <Avatar
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              width: 32,
              height: 32,
              fontSize: '1rem'
            }}
          >
            {getUserInitials(user.email)}
          </Avatar>
        ) : (
          <AccountCircle 
            sx={{
              color: 'grey.500',
              width: 32,
              height: 32
            }}
          />
        )}
      </IconButton>

      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        sx={{
          mt: 1.5,
          '& .MuiPaper-root': {
            minWidth: 200
          }
        }}
      >
        {user ? (
          // Authenticated user menu
          [
            <MenuItem key="user-info" disabled>
              <ListItemIcon>
                <Avatar
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    width: 24,
                    height: 24,
                    fontSize: '0.875rem'
                  }}
                >
                  {getUserInitials(user.email)}
                </Avatar>
              </ListItemIcon>
              <ListItemText>
                <Typography variant="body2" noWrap>
                  {user.email}
                </Typography>
              </ListItemText>
            </MenuItem>,
            <Divider key="divider" />,
            <MenuItem key="my-routes" onClick={handleMyRoutes}>
              <ListItemIcon>
                <Route fontSize="small" />
              </ListItemIcon>
              <ListItemText>My Routes</ListItemText>
            </MenuItem>,
            <MenuItem key="settings" onClick={handleSettings}>
              <ListItemIcon>
                <Settings fontSize="small" />
              </ListItemIcon>
              <ListItemText>Settings</ListItemText>
            </MenuItem>,
            <MenuItem key="logout" onClick={handleSignOut}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              <ListItemText>Sign Out</ListItemText>
            </MenuItem>
          ]
        ) : (
          // Guest user menu
          [
            <MenuItem key="signin" onClick={handleSignIn}>
              <ListItemIcon>
                <Login fontSize="small" />
              </ListItemIcon>
              <ListItemText>Sign In</ListItemText>
            </MenuItem>,
            <MenuItem key="signup" onClick={handleSignUp}>
              <ListItemIcon>
                <PersonAdd fontSize="small" />
              </ListItemIcon>
              <ListItemText>Sign Up</ListItemText>
            </MenuItem>
          ]
        )}
      </Menu>

      <AuthModal
        open={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        mode={authMode}
        onModeChange={setAuthMode}
      />
    </>
  );
};

export default UserMenu;
